import { motion } from "framer-motion";

const EASE = [0.45, 0.05, 0.25, 1];
const TURN = 0.9;

const Page = ({ page }) => (
  <img src={page.src} alt={`Page ${page.n}`} className="w-full h-full object-cover block bg-cream" draggable={false} data-testid={`flipbook-page-${page.n}`} />
);

// Soft shadow along the spine, like the curve of real paper into the binding.
const Gutter = ({ side }) => (
  <div
    className={`absolute inset-y-0 w-10 pointer-events-none ${side === "L" ? "right-0 bg-gradient-to-l" : "left-0 bg-gradient-to-r"} from-black/30 via-black/10 to-transparent`}
  />
);

const Half = ({ page, side, wide }) => (
  <div className={`absolute inset-y-0 ${wide ? `w-1/2 ${side === "L" ? "left-0" : "right-0"}` : "inset-x-0"} ${page ? "shadow-[0_30px_80px_rgba(0,0,0,0.55)]" : ""}`}>
    {page && <Page page={page} />}
    {page && wide && <Gutter side={side} />}
  </div>
);

// One side of the turning sheet; the fold shadow darkens as the sheet stands up and fades as it lands.
const Face = ({ page, back, side, wide }) => (
  <div className="absolute inset-0 [backface-visibility:hidden] bg-cream overflow-hidden" style={back ? { transform: "rotateY(180deg)" } : undefined}>
    {page && <Page page={page} />}
    {wide && <Gutter side={side} />}
    <motion.div
      className={`absolute inset-0 pointer-events-none ${side === "L" ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-black/60 via-black/20 to-transparent`}
      initial={{ opacity: back ? 0.7 : 0 }}
      animate={{ opacity: back ? [0.7, 0.35, 0] : [0, 0.55, 0.7] }}
      transition={{ duration: TURN, ease: EASE }}
    />
  </div>
);

const Leaf = ({ front, back, next, wide, onDone }) => {
  const from = wide ? 0 : next ? 0 : -180;
  const to = wide ? (next ? -180 : 180) : next ? -180 : 0;
  const pos = wide ? `w-1/2 ${next ? "right-0" : "left-0"}` : "inset-x-0";
  const origin = wide && !next ? "right center" : "left center";
  return (
    <motion.div
      className={`absolute inset-y-0 z-10 ${pos} [transform-style:preserve-3d]`}
      style={{ transformOrigin: origin }}
      initial={{ rotateY: from }}
      animate={{ rotateY: to }}
      transition={{ duration: TURN, ease: EASE }}
      onAnimationComplete={onDone}
      data-testid="flipbook-leaf"
    >
      <Face page={front} side={wide ? (next ? "R" : "L") : "R"} wide={wide} />
      {wide && <Face page={back} side={next ? "L" : "R"} wide={wide} back />}
      {/* cast shadow drawn on the page underneath while the sheet is in the air */}
      <motion.div
        className="absolute inset-0 -z-10 shadow-[0_0_60px_rgba(0,0,0,0.45)] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: TURN, ease: EASE }}
      />
    </motion.div>
  );
};

// Pages lying flat under the turning sheet, plus the sheet itself.
export const Spread = ({ views, at, flip, wide, onDone }) => {
  const a = views[at];
  if (!flip) {
    return wide ? (
      <>
        <Half page={a[0]} side="L" wide />
        <Half page={a[1]} side="R" wide />
      </>
    ) : (
      <Half page={a[0]} wide={false} />
    );
  }
  const b = views[flip.to];
  const next = flip.dir > 0;
  if (!wide) {
    return (
      <>
        <Half page={next ? b[0] : a[0]} wide={false} />
        <Leaf key={`${flip.from}-${flip.to}`} front={next ? a[0] : b[0]} next={next} wide={false} onDone={onDone} />
      </>
    );
  }
  return (
    <>
      <Half page={next ? a[0] : b[0]} side="L" wide />
      <Half page={next ? b[1] : a[1]} side="R" wide />
      <Leaf key={`${flip.from}-${flip.to}`} front={next ? a[1] : a[0]} back={next ? b[0] : b[1]} next={next} wide onDone={onDone} />
    </>
  );
};
